import { useEffect, useState } from 'react';
import { StoredPublicTender } from '~src/contexts/CachedPublicTender';
import { TenderCoreRegVM } from '~src/types/db';
import { loadPublicTenderCoreRegistration } from '~src/services/api/shares';

export type PublicTenderRequestStatus = 'available' | 'unavailable';

export type PublicTenderTableEntry = StoredPublicTender &
  TenderCoreRegVM & {
    publicTenderRequestStatus?: PublicTenderRequestStatus;
    publicTenderHttpCode?: number;
  };

type ExpiredCachedPublicTender = {
  tenderShareId: string;
};

export const usePublicTendersSimultaneously = (
  publicTendersListFromLocalStorage: StoredPublicTender[]
) => {
  const [publicTendersList, setPublicTendersList] = useState<
    PublicTenderTableEntry[]
  >([]);

  const [expiredPublicTendersList, setExpiredPublicTendersList] = useState<
    ExpiredCachedPublicTender[]
  >([]);

  useEffect(() => {
    const getPublicTendersListSimultaneously = async (
      publicTendersList: StoredPublicTender[]
    ) => {
      const publicTendersResult = await Promise.allSettled(
        publicTendersList.map(async ({ tenderShareId }) => {
          const publicTenderData = await loadPublicTenderCoreRegistration(
            null,
            {
              tenderShareId,
            }
          );

          return {
            ...publicTenderData,
            tenderShareId,
            publicTenderRequestStatus: 'available' as PublicTenderRequestStatus,
            publicTenderHttpCode: 200,
          };
        })
      );

      const activePublicTenders: PublicTenderTableEntry[] = [];
      const expiredPublicTenders: ExpiredCachedPublicTender[] = [];

      let i = 0;

      for (const publicTenderResult of publicTendersResult) {
        const currentPublicTender = publicTendersList[i];

        if (
          publicTenderResult.status === 'fulfilled' &&
          !!publicTenderResult.value
        ) {
          activePublicTenders.push(publicTenderResult.value);
        } else if (
          publicTenderResult.status === 'rejected' &&
          currentPublicTender?.tenderShareId
        ) {
          const publicTenderHttpCode =
            (publicTenderResult.reason as any)?.response?.status || undefined;

          activePublicTenders.push({
            ...currentPublicTender,
            tenderShareId: currentPublicTender.tenderShareId,
            publicTenderRequestStatus: 'unavailable',
            publicTenderHttpCode,
          } as PublicTenderTableEntry);

          expiredPublicTenders.push({
            tenderShareId: currentPublicTender.tenderShareId,
          });
        }

        i++;
      }

      setPublicTendersList(activePublicTenders);
      setExpiredPublicTendersList(expiredPublicTenders);
    };

    if (publicTendersListFromLocalStorage.length > 0) {
      getPublicTendersListSimultaneously(publicTendersListFromLocalStorage);
      return;
    }

    setPublicTendersList([]);
    setExpiredPublicTendersList([]);
  }, [publicTendersListFromLocalStorage]);

  return {
    publicTendersList,
    expiredPublicTendersList,
  };
};
